import { ClerkProvider, useUser } from "@clerk/clerk-react";
import { dark } from "@clerk/themes";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import { initPostHog, identifyUser, resetUser, capturePageview } from "./lib/posthog.js";
import { TRPCProvider } from "./lib/providers.js";
import { initSentry, setSentryUser, resetSentryUser } from "./lib/sentry.js";
import { routeTree } from "./routeTree.gen";
import "./index.css";

// Init observability
initSentry();
initPostHog();

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const clerkAppearance = {
  baseTheme: dark,
  variables: {
    colorPrimary: "#26d9b5",
    colorBackground: "#1a1c23",
    colorInputBackground: "#101319",
    colorInputText: "#ffffff",
    colorText: "#ffffff",
    colorTextSecondary: "#a2a5ae",
    borderRadius: "0.75rem",
  },
  elements: {
    card: {
      backgroundColor: "#1a1c23",
      border: "1px solid #30333c",
    },
    headerTitle: {
      color: "#ffffff",
    },
    headerSubtitle: {
      color: "#a2a5ae",
    },
    socialButtonsBlockButton: {
      backgroundColor: "#30333c",
      border: "1px solid #30333c",
      "&:hover": {
        backgroundColor: "#4a4e5a",
      },
    },
    formButtonPrimary: {
      backgroundColor: "#26d9b5",
      color: "#101319",
      "&:hover": {
        backgroundColor: "#1fb89a",
      },
    },
    footerActionLink: {
      color: "#26d9b5",
      "&:hover": {
        color: "#4ddcbf",
      },
    },
    identityPreview: {
      backgroundColor: "#30333c",
    },
    formFieldInput: {
      backgroundColor: "#101319",
      border: "1px solid #30333c",
      "&:focus": {
        borderColor: "#26d9b5",
      },
    },
  },
};

// Create the router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
});

// Register the router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function PostHogIdentifier() {
  const { user, isSignedIn } = useUser();

  useEffect(() => {
    if (isSignedIn && user) {
      const email = user.primaryEmailAddress?.emailAddress ?? '';
      const username = user.username ?? '';
      identifyUser(user.id, { email, username });
      setSentryUser(user.id, { email, username });
    } else if (!isSignedIn) {
      resetUser();
      resetSentryUser();
    }
  }, [isSignedIn, user]);

  return null;
}

// Track pageviews on route changes
router.subscribe('onLoad', () => {
  capturePageview();
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      appearance={clerkAppearance}
      signInForceRedirectUrl="/app"
      signUpForceRedirectUrl="/app"
    >
      <TRPCProvider>
        <PostHogIdentifier />
        <RouterProvider router={router} />
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#1a1c23",
              border: "1px solid #30333c",
              color: "#ffffff",
            },
          }}
        />
      </TRPCProvider>
    </ClerkProvider>
  </StrictMode>,
);
